import { handleVersionMismatch } from 'actions/versionSync';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { VersionMismatch } from 'services/BaseService';
import PaymentService from 'services/PaymentService';
import PurchaseSubscriptionComponent from 'components/PurchaseSubscription/PurchaseSubscriptionComponent';
import { IPaymentPlan } from 'types/payment';
import Loading from 'components/Loading/index';
import { fetch as fetchSubscription } from 'actions/subscription';
import getDefaultPayPalOptions from 'components/Payment/getDefaultPayPalOptions';
import { fetchActions } from 'actions/actions';
import { fetch as fetchCards } from 'actions/cards';

export interface Props {
  dispatch: Function,
  onNext: (message?: string) => void,
}

export function Payment({ dispatch, onNext }: Props) {
  const paymentServiceRef = useRef(new PaymentService());

  const [clientRequestToken, setClientRequestToken] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [hasFetched, setHasFetched] = useState(false);
  const [instance, setInstance] = useState<any>(null);
  const [recaptchaResponse, setRecaptchaResponse] = useState<any>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForInstance, setIsWaitingForInstance] = useState(false);
  const [plans, setPlans] = useState<IPaymentPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('FLExceptionalFriendship-GBP');

  useEffect(() => {
    paymentServiceRef.current.fetchPlans()
      .then(({ data }) => {
        const { clientRequestToken, plans } = data;
        setClientRequestToken(clientRequestToken);
        setPlans(plans);
        setHasFetched(true);
      })
      .catch((e) => {
        if (e instanceof VersionMismatch) {
          dispatch(handleVersionMismatch(e));
          return;
        }
        throw e;
      });
  }, [
    dispatch,
  ]);

  const onInstance = useCallback((instance) => {
    setInstance(instance);
    setIsWaitingForInstance(false);
  }, []);
  const onReCaptchaChange = useCallback(setRecaptchaResponse, []);

  const onSelect = useCallback((e) => {
    setSelectedPlanId(e.target.value);
  }, []);

  const onSubmit = useCallback((e) => {
      if (isSubmitting) {
        return;
      }

      e.preventDefault();
      setIsSubmitting(true);
      if (instance !== null && selectedPlanId !== undefined) {
        const planId = selectedPlanId;
        (async () => {
          try {
            // Get a payment method and make the API request to subscribe
            const { nonce } = await instance.requestPaymentMethod();
            const { data } = await paymentServiceRef.current.purchasePlan({ nonce, recaptchaResponse, planId });
            // Fetch subscription details; we don't need to await this since it's just updating the global store
            await dispatch(fetchSubscription());
            const { message, isSuccess } = data;
            // Handle success; we need to update actions & card count
            if (isSuccess) {
              dispatch(fetchActions());
              dispatch(fetchCards());
              onNext(message);
              return;
            }
            // Handle failure
            setIsSubmitting(false);
            setErrorMessage(message);
          } catch (e) {
            if (e instanceof VersionMismatch) {
              dispatch(handleVersionMismatch(e));
              return;
            }
            throw e;
          }
        })();
      }

    }, [
      dispatch,
      instance,
      isSubmitting,
      onNext,
      recaptchaResponse,
      selectedPlanId,
    ],
  );

  if (!hasFetched || isWaitingForInstance) {
    return <Loading spinner />;
  }

  return (
    <PurchaseSubscriptionComponent
      isSubmitting={isSubmitting}
      isWaitingForInstance={isWaitingForInstance}
      message={errorMessage}
      onReCaptchaChange={onReCaptchaChange}
      onInstance={onInstance}
      onSelect={onSelect}
      onSetNonce={() => {
      }} // No debug tools in the gate event modal
      onSubmit={onSubmit}
      options={getDefaultPayPalOptions(clientRequestToken)}
      plans={plans}
      selectedPlanId={selectedPlanId}
    />
  );
}

Payment.displayName = 'Payment';

export default connect()(Payment);